import type { ThemeVariant } from '../../contract.js';
import { defaultLight, defaultDark } from '../default/variants.js';

/**
 * Cyber family — cyberpunk theme with harmonic-wave shader.
 *
 * Dark variant: hot magenta accent on dark navy. The shader does
 * the visual heavy lifting (150 oscillating orbs across three
 * channels + CRT-style scanline modulation); chrome stays
 * supporting so the canvas painting reads as the focus surface.
 *
 * Light variant: pastel-cyber interpretation — dusty violet
 * accent on lavender cream. Lighter palette across the board so
 * the pastel shader composition reads against pale chrome
 * without one fighting the other.
 *
 * `charging` stays brand #21c163 on both variants per the
 * literal-brand-on-session-state convention every other theme
 * follows. The shader's per-variant uniforms drive the channel
 * colors directly (CYBER_UNIFORMS in shaders/cyber.ts) — the
 * accent token here drives PWA chrome (CTA button bg, focus
 * outlines, etc.) separately from the shader composition.
 */
export const cyberLight: ThemeVariant = {
  mode: 'light',
  tokens: {
    ...defaultLight.tokens,
    colors: {
      ...defaultLight.tokens.colors,
      // Lavender cream bg — matches the shader's uBgColorA
      // exactly so canvas + surrounding chrome read as a
      // continuous pale-cyber surface. Slight violet shift on
      // surface2 ties the multi-card surfaces to the family
      // palette without dropping into full saturation.
      bg: '#F8F5FC',
      surface: '#FFFFFF',
      surface2: '#F0EAF6',
      border: '#E0D5EC',
      borderStrong: '#C0B0D8',
      // Dusty violet accent — readable on the lavender bg without
      // going neon. Saturation kept lower than the dark variant's
      // hot magenta because pastel-on-pale reads punchy at lower
      // saturation than neon-on-dark.
      accent: '#8E4FB0',
      accentSoft: 'rgba(142, 79, 176, 0.14)',
      // Near-bg text on the dusty-violet accent. Strong contrast
      // (WCAG ~6:1) without competing with the lavender palette.
      onAccent: '#F8F5FC',
      charging: '#21c163',
      // Lavender halo on the charging-state glow — picks up the
      // pastel-lavender secondary channel from the shader so
      // chrome + canvas share the same accent vocabulary.
      chargingGlow: 'rgba(165, 140, 230, 0.45)',
      mute: '#9080A8',
      text: '#2A1F38',
      textMute: '#6A5680',
      // Violet 3-stop card glow stack. Low alphas because the
      // lavender bg only needs a faint hue lift to read the
      // glow.
      cardGlow:
        '0 0 0 1px color-mix(in srgb, #8E4FB0 14%, transparent),'
        + ' 0 0 18px -4px color-mix(in srgb, #8E4FB0 18%, transparent),'
        + ' 0 6px 20px -10px color-mix(in srgb, #8E4FB0 16%, transparent)',
      cardGlowSubtle:
        '0 0 0 1px color-mix(in srgb, #8E4FB0 7%, transparent),'
        + ' 0 0 10px -6px color-mix(in srgb, #8E4FB0 10%, transparent),'
        + ' 0 3px 10px -8px color-mix(in srgb, #8E4FB0 7%, transparent)',
      // Widget + chrome glass — lavender cream bg with violet
      // ring tint. Reduced blur (defaultLight convention) so the
      // pale chrome doesn't read as muddy fog.
      widgetBg: 'rgba(255, 255, 255, 0.82)',
      widgetBorder: 'rgba(42, 31, 56, 0.08)',
      widgetShadow:
        '0 8px 24px rgba(42, 31, 56, 0.10),'
        + ' 0 0 0 1px rgba(142, 79, 176, 0.12)',
      chromeBg: 'rgba(252, 250, 254, 0.94)',
      chromeBorder: 'rgba(42, 31, 56, 0.06)',
      chromeShadow: '0 4px 16px rgba(42, 31, 56, 0.10)',
    },
  },
};

export const cyberDark: ThemeVariant = {
  mode: 'dark',
  tokens: {
    ...defaultDark.tokens,
    colors: {
      ...defaultDark.tokens.colors,
      // Dark navy bg — matches the shader's uBgColorA (rgba(0.04,
      // 0.06, 0.14) in shader space, #0A0E26 in hex) so canvas +
      // chrome read as a continuous void surface broken only by
      // the harmonic-wave orbs.
      bg: '#0A0E26',
      surface: '#13182E',
      surface2: '#1E2440',
      border: '#2A3050',
      borderStrong: '#3E456A',
      // Hot magenta accent — the dominant channel color from the
      // shader's uChannelColor1. Bright on dark navy gives CTA
      // pills strong presence without competing with the shader
      // canvas inside the card.
      accent: '#F233D9',
      accentSoft: 'rgba(242, 51, 217, 0.16)',
      // Near-navy text on the bright magenta accent. WCAG ~6:1.
      // Matches the variant's bg so CTA-text shares the page-
      // surface hue.
      onAccent: '#0A0E26',
      charging: '#21c163',
      // Electric-purple halo on the charging-state glow — picks
      // up the secondary channel color (uChannelColor2) so chrome
      // + canvas share the same accent vocabulary.
      chargingGlow: 'rgba(140, 77, 242, 0.55)',
      mute: '#4A5070',
      text: '#F0E8FA',
      textMute: '#9080B8',
      // Magenta 3-stop card glow stack. Same shape as Default
      // dark / Luce dark for visual consistency; only the hue
      // swaps to match the family identity.
      cardGlow:
        '0 0 0 1px color-mix(in srgb, #F233D9 20%, transparent),'
        + ' 0 0 24px -4px color-mix(in srgb, #F233D9 32%, transparent),'
        + ' 0 8px 32px -12px color-mix(in srgb, #F233D9 28%, transparent)',
      cardGlowSubtle:
        '0 0 0 1px color-mix(in srgb, #F233D9 10%, transparent),'
        + ' 0 0 14px -6px color-mix(in srgb, #F233D9 16%, transparent),'
        + ' 0 4px 12px -8px color-mix(in srgb, #F233D9 12%, transparent)',
      // Widget + chrome glass — dark navy bg with magenta ring
      // tint. Heavy dense-glass blur matching defaultDark since
      // popovers / chrome sit over the navy surface.
      widgetBg: 'rgba(19, 24, 46, 0.78)',
      widgetBorder: 'rgba(242, 51, 217, 0.12)',
      widgetShadow:
        '0 12px 32px rgba(0, 0, 0, 0.55),'
        + ' 0 0 0 1px rgba(242, 51, 217, 0.10)',
      chromeBg: 'rgba(19, 24, 46, 0.92)',
      chromeBorder: 'rgba(242, 51, 217, 0.10)',
      chromeShadow: '0 8px 24px rgba(0, 0, 0, 0.60)',
    },
  },
};
